import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { Layout } from 'antd';
import Axios from 'axios';
import _ from 'lodash';

import styled from 'styled-components';

import { deaccentizeandlowerize } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';
import { UserContext } from '@contexts/UserContext';

import { modelFullName } from '@hooks/API/Contact/UseContactsList';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import ContactsFiltersSidebar from '@components/ContactsSelector/Common/ContactsFiltersSidebar';
import ModelsWall from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsWall';
import { convertFiltersToParams } from '@components/Filters/FilterForm/FilterUtils';
import NetworkAgencySelector from '@components/Gui/NetworkAgencySelector';
import NwDrawer from '@components/Gui/NwDrawer';
import { StyledLayout } from '@components/Gui/StyledComponent';

import { useContactsSelector, useContactsSelectorDispatch } from '../Common/ContactsSelectorContext';

const { Content, Sider } = Layout;

const SiderStyled = styled(Sider)`
    height: calc(100vh - 60px);
    background-color: #fff;
    z-index: 1030;
    box-shadow:
        0 0.125rem 9.375rem rgba(90, 97, 105, 0.1),
        0 0.25rem 0.5rem rgba(90, 97, 105, 0.12),
        0 0.9375rem 1.375rem rgba(90, 97, 105, 0.1),
        0 0.4375rem 2.1875rem rgba(165, 182, 201, 0.1);
    position: fixed;
    padding: 1em;
    overflow: auto;
`;

const SelectorLayout = styled(Layout)`
    margin-left: ${(props) => (props.$size === 'big' ? '360px' : props.$size === 'small' ? '220px' : '300px')};
    margin-left: ${(props) => props.$type === 'compact' && 0};
`;

const WallWrapper = styled.div`
    position: relative;
`;

const ModelsSelectorAccounting = () => {
    const { selection, context, additional } = useContactsSelector();
    const { selectedNetworkAgencyID, selectedAccountingGroupID } = additional;

    const cs_dispatch = useContactsSelectorDispatch();

    const { cachedList } = useContext(ListContext);
    //const { currentUser } = useContext(UserContext);

    const { activeSet, filters, listType, quickFilters } = selection;

    const [lastCallSignature, setLastCallSignature] = useState(null);

    const { currentUserID } = context;

    const location = useLocation();

    const breakpoints = useNwBreakPoints();
    const isMobile = breakpoints === 1;

    const [isLoading, setIsLoading] = useState(false);

    const [items, setItems] = useState([]);

    //const [networkAgencyID, setNetworkAgencyID] = useState(additional.selectedNetworkAgencyID);

    const onChangeSelectedNetworkAgency = (nwAgID) => {
        cs_dispatch({
            type: 'UpdateSelectedNetworkAgencyID',
            selectedNetworkAgencyID: nwAgID,
        });
    };

    const siderWidth = breakpoints < 3 ? 200 : 300;
    const layoutSize = breakpoints < 3 ? 'small' : 'medium';

    const [fetchingItems, setFetchingItems] = useState(false);

    const currentAccGroup = cachedList.accountingGroups.items.find((ag) => ag.ID === selectedAccountingGroupID);

    const fetchItems = async () => {
        setFetchingItems(true);
        const convertedFilters = convertFiltersToParams(filters.active, null, currentUserID);
        const filtersToSend = {
            // DepartmentID: 0,
            // BillingCountry: 'string',
            // Payment: {
            //     Year: 0,
            // },
        };

        // if (convertedFilters.NetworkAgencyID) {
        //     filtersToSend.NetworkAgencyID = convertedFilters.NetworkAgencyID;
        // } else {
        //     filtersToSend.NetworkAgencyID = currentUser.ActiveContext.accGroupId;
        // }
        filtersToSend.NetworkAgencyID = selectedNetworkAgencyID;

        if (convertedFilters.InAgencyFrom) {
            filtersToSend.InagencyFrom = {
                startDate: convertedFilters.InAgencyFrom[0],
                endDate: convertedFilters.InAgencyFrom[1],
            };
        }
        if (convertedFilters.Agency) {
            filtersToSend.Agency = {
                ...convertedFilters.Agency,
            };
        }
        if (convertedFilters.Represented) {
            filtersToSend.Represented = convertedFilters.Represented;
        }
        if (convertedFilters.InTown) {
            filtersToSend.InTown = convertedFilters.InTown;
        }
        if (convertedFilters.InvoiceType) {
            filtersToSend.InvoiceType = convertedFilters.InvoiceType;
        }
        if (convertedFilters.HasVat !== null) {
            filtersToSend.HasVat = convertedFilters.HasVat;
        }
        if (convertedFilters.Storage !== null) {
            filtersToSend.Storage = convertedFilters.Storage;
        }
        if (convertedFilters.CommissionRange) {
            if (convertedFilters.CommissionRange.length === 2) {
                filtersToSend.AgencyCommissionRange = {
                    Min: convertedFilters.CommissionRange[0],
                    Max: convertedFilters.CommissionRange[1],
                };
            }
        }
        if (convertedFilters.Nationality) {
            filtersToSend.Nationality = convertedFilters.Nationality;
        }
        if (convertedFilters.customFields) {
            filtersToSend.CustomFields = { ...convertedFilters.customFields };
        }
        if (convertedFilters.Document) {
            if (convertedFilters.Document.length === 2) {
                if (convertedFilters.Document[0]) {
                    filtersToSend.Document = {
                        DocumentType: convertedFilters.Document[0],
                        Expired: convertedFilters.Document[1],
                    };
                }
            }
        }
        if (convertedFilters.YearlyDocument) {
            if (convertedFilters.YearlyDocument.length === 2) {
                if (convertedFilters.YearlyDocument[0]) {
                    filtersToSend.YearlyDocument = {
                        DocumentType: convertedFilters.YearlyDocument[0],
                        Year: convertedFilters.YearlyDocument[1],
                    };
                }
            }
        }
        if (convertedFilters.Statement) {
            if (convertedFilters.Statement.length > 0) {
                if (convertedFilters.Statement[0]) {
                    filtersToSend.Statement = { Year: null, Family: null, TransactionLabelID: null, YearOperator: null };
                    if (convertedFilters.Statement[0] !== 'any') {
                        filtersToSend.Statement.Family = convertedFilters.Statement[0];
                    }
                    if (convertedFilters.Statement[1]) {
                        filtersToSend.Statement.Year = convertedFilters.Statement[1];
                    }
                    if (convertedFilters.Statement[2]) {
                        filtersToSend.Statement.TransactionLabelID = convertedFilters.Statement[2];
                    }
                    if (convertedFilters.Statement[3]) {
                        filtersToSend.Statement.YearOperator = convertedFilters.Statement[3];
                    }
                }
            }
        }
        if (
            convertedFilters.AgeRange &&
            convertedFilters.AgeRange.length === 2 &&
            (convertedFilters.AgeRange[0] !== null || convertedFilters.AgeRange[1] !== null)
        ) {
            filtersToSend.AgeRange = {
                Min: convertedFilters.AgeRange[0],
                Max: convertedFilters.AgeRange[1],
            };
        }
        if (convertedFilters.Published) {
            filtersToSend.Published = convertedFilters.Published;
        }
        if (convertedFilters.DepartmentID) {
            filtersToSend.DepartmentID = convertedFilters.DepartmentID;
        }

        //get a string signature of the filters
        // const filtersSignature = JSON.stringify(filtersToSend);
        // if (filtersSignature !== lastCallSignature) {
        //     setLastCallSignature(filtersSignature);
        // } else {
        //     return;
        // }

        const results = await Axios.post('/models/accountingsearch', filtersToSend);
        setItems(results);
        setFetchingItems(false);
    };

    const [debouncedNameFilter, setDebouncedNameFilter] = useState(quickFilters.nameFilter);
    useDebounce(
        () => {
            if (quickFilters.nameFilter !== debouncedNameFilter) {
                setDebouncedNameFilter(quickFilters.nameFilter);
            }
        },
        300,
        [quickFilters.nameFilter]
    );

    const sortedAndFilteredItems = useMemo(() => {
        if (fetchingItems) {
            return [];
        }

        let templist = items;

        let normalizedNameFilter = debouncedNameFilter ? deaccentizeandlowerize(debouncedNameFilter) : null;
        templist = templist.filter((item) => {
            let result = true;
            if (normalizedNameFilter) {
                //nameFilter
                const normalizedItemName = deaccentizeandlowerize(item.Name);
                const normalizedItemSurname = item.Surname ? deaccentizeandlowerize(item.Surname) : '';
                const normalizedItemLegalName = item.LegalName ? deaccentizeandlowerize(item.LegalName) : '';
                const normalizedItemLegalSurname = item.LegalSurname ? deaccentizeandlowerize(item.LegalSurname) : '';
                result =
                    normalizedItemName.startsWith(normalizedNameFilter) ||
                    (normalizedItemSurname && normalizedItemSurname.startsWith(normalizedNameFilter)) ||
                    (normalizedItemName + ' ' + normalizedItemSurname).startsWith(normalizedNameFilter) ||
                    (normalizedItemLegalName && normalizedItemLegalName.startsWith(normalizedNameFilter)) ||
                    (normalizedItemLegalSurname && normalizedItemLegalSurname.startsWith(normalizedNameFilter)) ||
                    (normalizedItemLegalName &&
                        normalizedItemLegalSurname &&
                        (normalizedItemLegalName + ' ' + normalizedItemLegalSurname).startsWith(normalizedNameFilter));
            }
            return result;
        });

        const sortByName = (list) => {
            return list.sort((a, b) =>
                modelFullName(a).localeCompare(modelFullName(b), 'en', {
                    caseFirst: 'upper',
                })
            );
        };

        //sorting models
        if (isMobile) {
            templist = sortByName(templist);
        } else {
            // if (listType === 'folders') {
            //     if (quickFilters.sortFolder === "Name") { //!editFolder &&
            //         templist = sortByName(templist)
            //     }
            // } else {
            switch (quickFilters.sort) {
                case 'MotherAgency':
                    templist = _.sortBy(templist, [
                        function (o) {
                            return o.MotherAgencyName ? deaccentizeandlowerize(o.MotherAgencyName.trim()) : 'zzz';
                        },
                    ]);
                    break;
                case 'ID':
                    templist = _.sortBy(templist, [
                        function (o) {
                            return o.ID;
                        },
                    ]);
                    break;
                case 'InAgencyFrom':
                    templist = _.sortBy(templist, [
                        function (o) {
                            return o.InAgencyFrom;
                        },
                    ]);
                    break;
                default:
                    templist = sortByName(templist);
            }
            //}
        }

        return templist;
    }, [items, debouncedNameFilter, quickFilters.sort, isMobile, fetchingItems]); //quickFilters.sortFolder,

    // const filterItemsByFolder = () => {
    //     const folder = folders.openFolders.find((folder) => folder.ID.toString() === activeSet);
    //     if (folder) {
    //         setFilteredIDs(folder.items);
    //     }
    // };

    useEffect(() => {
        if (listType === 'smart-lists' && activeSet && filters && filters.active) {
            fetchItems();
        }
        // if (listType === 'folders') {
        //     filterItemsByFolder();
        // }
    }, [filters.active, listType, activeSet, selectedNetworkAgencyID]);

    // useEffect(() => {
    //     if ((listType === 'smart-lists' && activeSet && filters && filters.active) || listType === 'folders') {
    //         if (filteredIDs && Array.isArray(filteredIDs)) {
    //             cs_dispatch({
    //                 type: 'UpdateSavedWallIDs',
    //                 savedWallIDs: [...filteredIDs],
    //             });
    //         }
    //     }
    // }, [filteredIDs, folders]);

    // const [debouncedNameFilter, setDebouncedNameFilter] = useState(quickFilters.nameFilter);
    // useDebounce(
    //     () => {
    //         if (quickFilters.nameFilter !== debouncedNameFilter) {
    //             setDebouncedNameFilter(quickFilters.nameFilter);
    //         }
    //     },
    //     300,
    //     [quickFilters.nameFilter]
    // );

    // const isLoadingItems = () => {
    //     if (fetchingModels) {
    //         return true;
    //     }
    //     return false;
    // };

    return (
        <>
            <StyledLayout>
                {breakpoints > 2 ? (
                    <SiderStyled width={siderWidth}>
                        {currentAccGroup.NetworkAgencies.length > 1 && (
                            <div style={{ marginBottom: '.75rem', marginTop: '.5rem' }}>
                                <NetworkAgencySelector
                                    accountingGroupID={selectedAccountingGroupID}
                                    value={selectedNetworkAgencyID}
                                    onChange={onChangeSelectedNetworkAgency}
                                />
                            </div>
                        )}
                        <ContactsFiltersSidebar itemType="model" projectModels={[]} />
                    </SiderStyled>
                ) : (
                    <NwDrawer visible={false}>
                        <ContactsFiltersSidebar itemType="model" projectModels={[]} />
                    </NwDrawer>
                )}
                <SelectorLayout $size={layoutSize} $type={breakpoints < 3 && 'compact'}>
                    <Content>
                        <WallWrapper>
                            <ModelsWall
                                list={sortedAndFilteredItems}
                                inSideBar={false}
                                isLoading={isLoading || fetchingItems}
                            />
                        </WallWrapper>
                    </Content>
                </SelectorLayout>
            </StyledLayout>
        </>
    );
};

export default ModelsSelectorAccounting;
